import React, { useState, useEffect, useRef } from "react"
import { Calendar, Clock, ChevronDown, Check } from "lucide-react"

const DynamicDateSelector = ({ 
  value, 
  onChange, 
  onCustomPeriodChange,
  customDays,
  setCustomDays,
  customPeriod,
  setCustomPeriod,
  showCustomInput,
  setShowCustomInput
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [dynamicDateOption, setDynamicDateOption] = useState(value || "today")
  
  // Local state for custom period inputs - allows empty values while editing
  const [localCustomDays, setLocalCustomDays] = useState(customDays.toString())
  const [localCustomPeriod, setLocalCustomPeriod] = useState(customPeriod)

  const dropdownRef = useRef(null)

  // Update local state when value prop changes
  useEffect(() => {
    if (value !== undefined) {
      setDynamicDateOption(value)
    }
  }, [value])

  // Sync local state when parent props change
  useEffect(() => {
    setLocalCustomDays(customDays.toString())
  }, [customDays])

  useEffect(() => {
    setLocalCustomPeriod(customPeriod)
  }, [customPeriod])

  // Add wheel event handling for proper scrolling
  useEffect(() => {
    if (!isOpen) return

    const handleWheel = (e) => {
      // Find the scrollable container
      const scrollableContainer = e.target.closest('.assignment-selector-dropdown')
      if (scrollableContainer) {
        e.stopPropagation()
        // Don't prevent default - let the browser handle scrolling
      }
    }

    // Add event listener to the dropdown
    const dropdown = document.querySelector('.assignment-selector-dropdown')
    if (dropdown) {
      dropdown.addEventListener('wheel', handleWheel, { passive: false })
    }

    return () => {
      if (dropdown) {
        dropdown.removeEventListener('wheel', handleWheel)
      }
    }
  }, [isOpen])

  // Add click-outside handler to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setTimeout(() => {
          setIsOpen(false)
        }, 10)
      }
    }

    const events = ['mousedown', 'click', 'touchstart']
    
    events.forEach(eventType => {
      document.addEventListener(eventType, handleClickOutside, true)
    })

    return () => {
      events.forEach(eventType => {
        document.removeEventListener(eventType, handleClickOutside, true)
      })
    }
  }, [isOpen])

  // Dynamic date options
  const dynamicDateOptions = [
    { value: "today", label: "Day of Task Creation", icon: <Calendar size={14} /> },
    { value: "tomorrow", label: "Next Day After Task Creation", icon: <Calendar size={14} /> },
    { value: "next_business_day", label: "Next Business Day After Task Creation", icon: <Calendar size={14} /> },
    { value: "end_of_week", label: "End of Week (Friday) of Task Creation", icon: <Calendar size={14} /> },
    { value: "end_of_month", label: "End of Month of Task Creation", icon: <Calendar size={14} /> },
    { value: "end_of_quarter", label: "End of Quarter of Task Creation", icon: <Calendar size={14} /> },
    { value: "1_week", label: "1 Week After Task Creation", icon: <Clock size={14} /> },
    { value: "2_weeks", label: "2 Weeks After Task Creation", icon: <Clock size={14} /> },
    { value: "30_days", label: "30 Days After Task Creation", icon: <Clock size={14} /> },
    { value: "60_days", label: "60 Days After Task Creation", icon: <Clock size={14} /> },
    { value: "90_days", label: "90 Days After Task Creation", icon: <Clock size={14} /> },
    { value: "custom", label: "Custom Period...", icon: <Clock size={14} /> },
  ]

  const handleOptionChange = (option) => {
    setDynamicDateOption(option)
    onChange(option)

    if (option === "custom") {
      setShowCustomInput(true)
    } else {
      setShowCustomInput(false)
    }
  }

  const getDisplayText = () => {
    if (!dynamicDateOption) {
      return "Select a dynamic date..."
    }

    const found = dynamicDateOptions.find((opt) => opt.value === dynamicDateOption)
    return found ? found.label : dynamicDateOption
  }

  const getSelectedIcon = () => {
    if (!dynamicDateOption) {
      return <Calendar size={14} />
    }

    const found = dynamicDateOptions.find((opt) => opt.value === dynamicDateOption)
    return found ? found.icon : <Calendar size={14} />
  }

  return (
    <div className="relative">
      <button
        className="w-full px-3 py-2 text-left border rounded-md flex items-center justify-between bg-white text-sm hover:bg-gray-50 dark:bg-[#1f2937] dark:text-white dark:border-gray-600 dark:focus:outline-none"
        onClick={(e) => {
          e.stopPropagation()
          setIsOpen(!isOpen)
        }}
      >
        <div className="flex items-center gap-2 flex-1 min-w-0">
          {getSelectedIcon()}
          <span className="truncate">{getDisplayText()}</span>
        </div>
        <div 
          className="p-1 -m-1 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 rounded"
          onClick={(e) => {
            e.stopPropagation()
            setIsOpen(!isOpen)
          }}
        >
          <ChevronDown 
            size={14} 
            className={`text-gray-400 transition-transform flex-shrink-0 ${isOpen ? "rotate-180" : ""}`}
            onClick={(e) => {
              e.stopPropagation()
              setIsOpen(!isOpen)
            }}
          />
        </div>
      </button>

      {isOpen && (
        <div 
          ref={dropdownRef}
          className="assignment-selector-dropdown dynamic-date-select-menu absolute left-0 right-0 z-50 mt-1 bg-white border rounded-md shadow-lg dark:bg-[#1f2937] dark:text-white dark:border-gray-600 max-h-96 overflow-y-auto"
          onMouseEnter={(e) => {
            e.stopPropagation()
          }}
          onMouseLeave={(e) => {
            e.stopPropagation()
          }}
          onWheel={(e) => {
            e.stopPropagation()
          }}
        >
          <div className="py-1">
            {dynamicDateOptions.map((option) => (
              <button
                key={option.value}
                onClick={() => {
                  handleOptionChange(option.value)
                  if (option.value !== "custom") {
                    setIsOpen(false)
                  }
                }}
                className={`w-full px-3 py-2 text-left text-sm flex items-center gap-2 hover:bg-gray-50 dark:hover:bg-gray-700 ${
                  dynamicDateOption === option.value ? 'bg-blue-50 dark:bg-blue-900/20' : ''
                }`}
              >
                <div className={`w-4 h-4 rounded border-2 flex items-center justify-center ${
                  dynamicDateOption === option.value
                    ? 'border-blue-500 bg-blue-500'
                    : 'border-gray-300 dark:border-gray-600'
                }`}>
                  {dynamicDateOption === option.value && (
                    <Check className="h-3 w-3 text-white" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate flex items-center gap-2">
                    {option.icon}
                    {option.label}
                  </div>
                </div>
              </button>
            ))}
          </div>

          {showCustomInput && (
            <div className="border-t dark:border-gray-600 p-3">
              <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Custom Period
              </div>
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  {localCustomPeriod !== "day_of_month" && (
                    <input
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      value={localCustomDays}
                      onChange={(e) => {
                        // Allow only numbers and empty string
                        const value = e.target.value
                        if (value === '' || /^\d+$/.test(value)) {
                          setLocalCustomDays(value)
                        }
                      }}
                      onBlur={() => {
                        // On blur, update parent state with validated value
                        const numValue = parseInt(localCustomDays)
                        const finalValue = isNaN(numValue) || numValue < 0 ? 1 : numValue
                        setLocalCustomDays(finalValue.toString())
                        setCustomDays(finalValue)
                      }}
                      onKeyDown={(e) => {
                        // Handle Enter key to apply
                        if (e.key === 'Enter') {
                          e.target.blur()
                          setTimeout(() => {
                            onCustomPeriodChange()
                            setIsOpen(false)
                          }, 100)
                        }
                      }}
                      className="w-20 px-2 py-1 border border-gray-300 rounded text-sm dark:bg-[#1f2937] dark:text-white dark:border-gray-600"
                      placeholder="1"
                    />
                  )}
                  <select
                    value={localCustomPeriod}
                    onChange={(e) => {
                      const newPeriod = e.target.value
                      setLocalCustomPeriod(newPeriod)
                      setCustomPeriod(newPeriod)
                      
                      // Set default day to current day when switching to day_of_month
                      if (newPeriod === "day_of_month") {
                        const currentDay = new Date().getDate()
                        setLocalCustomDays(currentDay.toString())
                        setCustomDays(currentDay)
                      }
                    }}
                    className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm dark:bg-[#1f2937] dark:text-white dark:border-gray-600"
                  >
                    <option value="days">Days</option>
                    <option value="business_days">Business Days</option>
                    <option value="weeks">Weeks</option>
                    <option value="months">Months</option>
                    <option value="quarters">Quarters</option>
                    <option value="day_of_month">Day of Month</option>
                  </select>
                </div>
                
                {/* Day of Month Grid */}
                {localCustomPeriod === "day_of_month" && (
                  <div className="grid grid-cols-7 gap-1.5 p-2 bg-gray-50 dark:bg-gray-800 rounded">
                    {Array.from({ length: 31 }, (_, i) => i + 1).map((day) => (
                      <button
                        key={day}
                        onClick={(e) => {
                          e.stopPropagation()
                          setLocalCustomDays(day.toString())
                          setCustomDays(day)
                        }}
                        className={`px-2 py-1.5 text-xs font-medium rounded transition-all hover:scale-105 ${
                          parseInt(localCustomDays) === day
                            ? 'bg-blue-600 text-white shadow-md'
                            : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600'
                        }`}
                        title={`Day ${day} of month`}
                      >
                        {day}
                      </button>
                    ))}
                  </div>
                )}
                
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    // Ensure the input value is committed before applying
                    const numValue = parseInt(localCustomDays)
                    const finalValue = isNaN(numValue) || numValue < 0 ? 1 : numValue
                    setCustomDays(finalValue)
                    setTimeout(() => {
                      onCustomPeriodChange()
                      setIsOpen(false)
                    }, 50)
                  }}
                  className="w-full px-3 py-2 bg-blue-600 text-white rounded text-sm hover:bg-blue-700"
                >
                  Apply Custom Period
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default DynamicDateSelector
